import { Link } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import { Container } from "@/components/layout/Container";
import { Button } from "@/components/ui/button";

const LEGAL = {
  brand: "StaxED",
  appUrl: "https://app.staxed.in",
  supportEmail: "support@staxed.in",
  location: "India",
  effectiveDate: "15 July 2026",
};

type Section = {
  title: string;
  body: string[];
};

function LegalPage({
  title,
  intro,
  sections,
}: {
  title: string;
  intro: string;
  sections: Section[];
}) {
  return (
    <Container size="md" className="py-10 sm:py-14 lg:py-16">
      <div className="mx-auto max-w-3xl">
        <p className="text-sm font-medium text-brand">Effective {LEGAL.effectiveDate}</p>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          {title}
        </h1>
        <p className="mt-4 text-base leading-7 text-text-muted">{intro}</p>

        <div className="mt-10 space-y-9">
          {sections.map((section) => (
            <section key={section.title} className="border-t border-border pt-7">
              <h2 className="font-display text-xl font-semibold">{section.title}</h2>
              <div className="mt-3 space-y-3 text-sm leading-7 text-text-muted">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 border-t border-border pt-7">
          <h2 className="font-display text-xl font-semibold">Contact</h2>
          <p className="mt-3 text-sm leading-7 text-text-muted">
            Questions about this page can be sent to{" "}
            <a className="font-medium text-brand hover:underline" href={`mailto:${LEGAL.supportEmail}`}>
              {LEGAL.supportEmail}
            </a>
            .
          </p>
        </div>
      </div>
    </Container>
  );
}

export function TermsPage() {
  return (
    <LegalPage
      title="Terms and Conditions"
      intro={`These terms govern your use of ${LEGAL.brand}, a digital learning platform for CBSE students.`}
      sections={[
        {
          title: "Use of the service",
          body: [
            `${LEGAL.brand} provides digital study material, practice tools, progress tracking, and AI-assisted learning support for students. You may use the service only for lawful personal learning purposes.`,
            "You are responsible for keeping your account credentials secure. Do not share your login or paid access with other people.",
          ],
        },
        {
          title: "Subscriptions and payments",
          body: [
            "Paid access is offered as one Annual Plan for one selected class. The plan costs ₹2,999, paid upfront in one payment, and provides 365 days of access from successful activation.",
            "The crossed-out ₹4,999 price and approximately ₹250/month figure shown on the subscription page are reference and value-comparison information only. ₹250 cannot be paid monthly; Razorpay charges one ₹2,999 upfront payment. No additional GST is added at checkout.",
            "Payments are processed by Razorpay. We do not store card, UPI, net banking, or wallet credentials on our servers.",
            "The Annual Plan is a fixed-duration purchase with no automatic renewal or recurring mandate. Access remains active until the expiry date shown in the app; continuing afterward requires a new manual purchase.",
          ],
        },
        {
          title: "Class-wise access",
          body: [
            "A subscription unlocks content for the selected class only. Buying access for one class does not unlock other classes.",
            "If you choose the wrong class, contact support promptly. Changes may be reviewed case by case depending on usage and payment status.",
          ],
        },
        {
          title: "Learning content and AI tutor",
          body: [
            "We work to keep study material useful and accurate, but educational content and AI responses may contain mistakes. Students should use their textbooks, teachers, and official materials as the final reference.",
            "The AI tutor is intended for learning support and explanations. It should not be treated as professional, legal, medical, financial, or official academic advice.",
            "AI Tutor, AI-assisted doubts, and AI-generated tests are subject to fair-use limits. We may apply and adjust reasonable usage controls to protect service quality and prevent abuse.",
          ],
        },
        {
          title: "Intellectual property",
          body: [
            `The ${LEGAL.brand} interface, compiled study materials, learning flows, and original content are owned by or licensed to ${LEGAL.brand}. You may not copy, resell, scrape, redistribute, or commercially exploit the service without written permission.`,
            "You may download or view learning assets only where the app explicitly allows it and only for your own study use.",
          ],
        },
        {
          title: "Suspension or termination",
          body: [
            "We may suspend or restrict accounts that misuse the service, attempt unauthorized access, share paid access publicly, abuse payment systems, or violate these terms.",
            "If we discontinue a material feature, we will try to provide reasonable notice where practical.",
          ],
        },
        {
          title: "Limitation of liability",
          body: [
            `${LEGAL.brand} is provided on an as-available basis. To the maximum extent permitted by law, we are not liable for indirect losses, exam outcomes, temporary unavailability, data loss outside our reasonable control, or third-party payment/network failures.`,
          ],
        },
        {
          title: "Changes to these terms",
          body: [
            "We may update these terms as the product evolves. The latest version will be available on this page with the effective date shown above.",
          ],
        },
      ]}
    />
  );
}

export function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      intro={`This policy explains how ${LEGAL.brand} collects and uses information when you use the app.`}
      sections={[
        {
          title: "Information we collect",
          body: [
            "We collect account details such as name, email address, phone number where provided, class selection, school/city details where provided, and authentication records needed to operate your account.",
            "We collect learning activity such as chapters opened, study modes used, quiz attempts, progress, subscription status, and payment history references.",
            "When you use the AI tutor, we process your chat messages and chapter context so the service can respond and maintain conversation history.",
          ],
        },
        {
          title: "Payments",
          body: [
            "Payments are handled by Razorpay. We receive payment identifiers, amount, currency, status, method, order id, and related metadata needed to confirm subscriptions and support billing queries.",
            "We do not store full card numbers, UPI PINs, CVV, net banking passwords, or wallet credentials.",
          ],
        },
        {
          title: "How we use information",
          body: [
            "We use information to create and secure accounts, provide class-wise access, show learning progress, process subscriptions, send transactional emails, support users, prevent abuse, and improve the product.",
            "We may use aggregated, non-identifying analytics to understand product usage and improve learning experiences.",
          ],
        },
        {
          title: "Service providers",
          body: [
            "We use trusted service providers for hosting, authentication, payments, email delivery, storage, and AI functionality. These providers process information only as needed to provide their services to us.",
            "Current infrastructure includes AWS for hosting/auth/storage/email, Razorpay for payments, and Google AI services for AI tutor functionality.",
          ],
        },
        {
          title: "Data retention",
          body: [
            "We keep account, subscription, billing, and audit records for as long as needed to operate the service, comply with law, resolve disputes, and prevent fraud.",
            "AI conversation history may be retained for a limited period to let you resume conversations and to maintain service quality.",
          ],
        },
        {
          title: "Your choices",
          body: [
            "You can update many profile details from the app. You can contact support to request help with account access, correction, or deletion requests, subject to legal and operational retention requirements.",
            "You can stop using the service at any time. Some transactional or legal records may still be retained where required.",
          ],
        },
        {
          title: "Security",
          body: [
            "We use access controls, encrypted transport, managed cloud services, and payment-provider verification to protect user and payment-related data.",
            "No internet service can be guaranteed completely secure, so you should keep your password and email account protected.",
          ],
        },
        {
          title: "Children and students",
          body: [
            `${LEGAL.brand} is designed for school students. Where a parent or guardian purchases or manages access for a student, they are responsible for supervising use of the service as appropriate.`,
          ],
        },
      ]}
    />
  );
}

export function RefundPolicyPage() {
  return (
    <LegalPage
      title="Cancellation and Refund Policy"
      intro="This policy explains cancellation and refund handling for StaxED digital subscriptions."
      sections={[
        {
          title: "Digital subscription access",
          body: [
            "StaxED provides digital access to study content after successful payment confirmation. Access is usually activated immediately after Razorpay confirms the payment.",
            "Because the product is digital, there is no physical return process.",
          ],
        },
        {
          title: "Cancellation",
          body: [
            "The Annual Plan is an upfront, fixed-period purchase and does not automatically renew. You can simply choose not to purchase another plan after your current access period ends.",
            "Cancelling future use does not automatically create a refund for the current paid period.",
          ],
        },
        {
          title: "Refund eligibility",
          body: [
            "Refunds may be considered if you were charged but access was not activated, if there was a duplicate payment, or if a clear technical issue prevented use and we could not resolve it within a reasonable time.",
            "Refunds are generally not provided for change of mind, low usage, exam schedule changes, or after substantial use of paid content.",
          ],
        },
        {
          title: "Wrong class",
          body: [
            "If you accidentally bought access for the wrong class, contact support as soon as possible. We may help switch access or review a refund request depending on the payment, usage, and subscription status.",
          ],
        },
        {
          title: "Refund timeline",
          body: [
            "Approved refunds are initiated through Razorpay to the original payment method where possible. Bank, UPI, card, or wallet processing timelines depend on Razorpay and the issuing bank/payment provider.",
            "If a refund is issued, access may be revoked or adjusted for the refunded subscription.",
          ],
        },
        {
          title: "How to request help",
          body: [
            `Email ${LEGAL.supportEmail} with your StaxED account email, payment date, amount, Razorpay payment id if available, and a short description of the issue.`,
          ],
        },
      ]}
    />
  );
}

export function ShippingPolicyPage() {
  return (
    <LegalPage
      title="Shipping and Digital Delivery Policy"
      intro="StaxED is a digital learning subscription. We do not ship physical goods."
      sections={[
        {
          title: "No physical shipping",
          body: [
            "All StaxED products are delivered digitally through the web app. There are no physical books, parcels, courier deliveries, or shipping charges for the subscription.",
          ],
        },
        {
          title: "Delivery of access",
          body: [
            "After a successful Razorpay payment, subscription access is activated for the selected class in the StaxED account used during checkout.",
            "Most successful payments activate immediately. In rare cases, confirmation may take longer due to payment provider delays, webhook retries, or network issues.",
          ],
        },
        {
          title: "Access requirements",
          body: [
            `To use the service, you need an internet connection, a supported browser, and access to your ${LEGAL.brand} account at ${LEGAL.appUrl}.`,
            "If your payment is successful but access does not appear, contact support with your account email and payment details.",
          ],
        },
        {
          title: "Service availability",
          body: [
            "We try to keep the app available reliably, but access may occasionally be affected by maintenance, internet issues, browser/device issues, payment provider delays, or cloud provider outages.",
          ],
        },
      ]}
    />
  );
}

export function ContactPage() {
  return (
    <Container size="md" className="py-10 sm:py-14 lg:py-16">
      <div className="mx-auto max-w-3xl">
        <p className="text-sm font-medium text-brand">Support</p>
        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Contact Us
        </h1>
        <p className="mt-4 text-base leading-7 text-text-muted">
          Need help with your StaxED account, subscription, payment, or content access?
          Send us the details and we will help you sort it out.
        </p>

        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <section className="rounded-lg border border-border bg-surface p-5">
            <Mail className="h-5 w-5 text-brand" />
            <h2 className="mt-4 font-display text-lg font-semibold">Email support</h2>
            <p className="mt-2 text-sm leading-6 text-text-muted">
              For fastest help, include your account email, class, payment id if relevant,
              and a short description of the issue.
            </p>
            <Button className="mt-5" asChild>
              <a href={`mailto:${LEGAL.supportEmail}`}>{LEGAL.supportEmail}</a>
            </Button>
          </section>

          <section className="rounded-lg border border-border bg-surface p-5">
            <MapPin className="h-5 w-5 text-brand" />
            <h2 className="mt-4 font-display text-lg font-semibold">Business location</h2>
            <p className="mt-2 text-sm leading-6 text-text-muted">
              {LEGAL.brand} operates from {LEGAL.location} and provides digital learning
              subscriptions for students through the StaxED web app.
            </p>
            <Button className="mt-5" variant="secondary" asChild>
              <Link to="/subjects">Open StaxED</Link>
            </Button>
          </section>
        </div>

        <section className="mt-10 border-t border-border pt-7">
          <h2 className="font-display text-xl font-semibold">Support hours</h2>
          <p className="mt-3 text-sm leading-7 text-text-muted">
            We review support requests on business days and prioritize payment/access
            issues first. Response time may vary during holidays or high-volume periods.
          </p>
        </section>
      </div>
    </Container>
  );
}
